import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const PAGE_SIZE = 10;

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

/* ------------------------------------------------------------------ */
/*  Generic modal shell (matches BoardDetail modal styling)            */
/*  `fit`: a confirmation fits its message instead of DIALOG_HEIGHT.   */
/* ------------------------------------------------------------------ */
function Modal({ title, onClose, children, footer, maxWidth = "max-w-lg", fit = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={["relative z-10 flex w-full flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200", fit ? "" : DIALOG_HEIGHT, maxWidth].filter(Boolean).join(" ")}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add / Edit user modal                                              */
/* ------------------------------------------------------------------ */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function computeErrors({ userid, username, emailid, password, confirmPassword, isEdit }) {
  const errors = {};
  if (userid.trim().length === 0) errors.userid = "User ID is required.";
  if (username.trim().length === 0) errors.username = "Display Name is required.";
  if (emailid.trim().length === 0) errors.emailid = "Email is required.";
  else if (!EMAIL_PATTERN.test(emailid.trim())) errors.emailid = "Enter a valid email address.";
  if (!isEdit) {
    if (password.length === 0) errors.password = "Password is required.";
    else if (password.length < 8) errors.password = "Password must be at least 8 characters.";
    if (confirmPassword.length === 0) errors.confirmPassword = "Please confirm your password.";
    else if (confirmPassword !== password) errors.confirmPassword = "Passwords do not match.";
  }
  return errors;
}

function UserFormModal({ mode, initial, onClose, onSubmit, saving, error }) {
  const isEdit = mode === "edit";
  const [userid, setUserid] = useState(initial?.userid || "");
  const [username, setUsername] = useState(initial?.username || "");
  const [emailid, setEmailid] = useState(initial?.emailid || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const usernameRef = useRef(null);

  // When editing, User ID is disabled — jump focus to Display Name instead.
  useEffect(() => {
    if (isEdit) usernameRef.current?.focus();
  }, [isEdit]);

  const errors = computeErrors({ userid, username, emailid, password, confirmPassword, isEdit });
  const canSubmit = Object.keys(errors).length === 0;
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const showError = (field) => (touched[field] || submitAttempted) && errors[field];

  const submit = () => {
    setSubmitAttempted(true);
    if (!canSubmit || saving) return;
    const payload = {
      username: username.trim(),
      emailid: emailid.trim(),
    };
    if (!isEdit) {
      payload.userid = userid.trim();
      payload.password = password;
    }
    onSubmit(payload);
  };

  const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";
  const reqMark = <span className="text-red-500">*</span>;
  const errorTextCls = "mt-1 text-xs text-rose-600";
  const disabledInputCls = "cursor-not-allowed bg-slate-100 text-slate-500";

  return (
    <Modal
      title={isEdit ? "Edit user" : "Add user"}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <div>
          <label className={labelCls}>User ID {reqMark}</label>
          <input
            autoFocus={!isEdit}
            value={userid}
            onChange={(e) => setUserid(e.target.value)}
            onBlur={() => markTouched("userid")}
            disabled={isEdit}
            // placeholder="e.g. shanmugam"
            className={[inputCls, isEdit ? disabledInputCls : ""].join(" ")}
          />
          {showError("userid") && <p className={errorTextCls}>{errors.userid}</p>}
        </div>
        <div>
          <label className={labelCls}>Display Name {reqMark}</label>
          <input
            ref={usernameRef}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={() => markTouched("username")}
            // placeholder="Full name"
            className={inputCls}
          />
          {showError("username") && <p className={errorTextCls}>{errors.username}</p>}
        </div>
        <div>
          <label className={labelCls}>Email {reqMark}</label>
          <input
            value={emailid}
            onChange={(e) => setEmailid(e.target.value)}
            onBlur={() => markTouched("emailid")}
            // placeholder="you@company.com"
            type="email"
            className={inputCls}
          />
          {showError("emailid") && <p className={errorTextCls}>{errors.emailid}</p>}
        </div>
        {!isEdit && (
          <>
            <div>
              <label className={labelCls}>Password {reqMark}</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => markTouched("password")}
                // placeholder="at least 8 characters"
                type="password"
                autoComplete="new-password"
                className={inputCls}
              />
              {showError("password") && <p className={errorTextCls}>{errors.password}</p>}
            </div>
            <div>
              <label className={labelCls}>Confirm Password {reqMark}</label>
              <input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => markTouched("confirmPassword")}
                // placeholder="re-enter password"
                type="password"
                autoComplete="new-password"
                className={inputCls}
              />
              {showError("confirmPassword") && (
                <p className={errorTextCls}>{errors.confirmPassword}</p>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  View user modal (read-only)                                        */
/* ------------------------------------------------------------------ */
function ViewUserModal({ user, onClose }) {
  return (
    <Modal title="User details" onClose={onClose}>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">User ID</dt>
          <dd className="font-medium text-slate-900">{user.userid || "—"}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Display Name</dt>
          <dd className="font-medium text-slate-900">{user.username}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Email</dt>
          <dd className="font-medium text-slate-900">{user.emailid}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Created Date</dt>
          <dd className="font-medium text-slate-900">{formatDate(user.createddate)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Modified Date</dt>
          <dd className="font-medium text-slate-900">{formatDate(user.modifieddate)}</dd>
        </div>
      </dl>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirm modal                                                      */
/* ------------------------------------------------------------------ */
/* No / Yes, as every confirmation (ConfirmModal.js). */
function ConfirmModal({ title, message, onCancel, onConfirm, busy }) {
  return (
    <Modal
      fit
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Working…" : "Yes"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-700">{message}</p>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Row action button (matches TestCaseRow ActionButton)               */
/* ------------------------------------------------------------------ */
function ActionButton({ icon: Glyph, label, onClick, primary = false, danger = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
        primary
          ? "border-brand-600 bg-brand-600 text-white hover:bg-brand-700"
          : danger
          ? "border-rose-200 bg-card text-rose-500 hover:border-rose-300 hover:bg-rose-50"
          : "border-slate-200 bg-card text-slate-500 hover:border-slate-300 hover:text-slate-800",
      ].join(" ")}
    >
      <Glyph className="h-4 w-4" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Users view                                                         */
/* ------------------------------------------------------------------ */
export default function UsersView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [formMode, setFormMode] = useState(null); // "add" | "edit" | null
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, action: async () => void }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_BASE_URL}users`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      const list = Array.isArray(body?.data) ? body.data : [];
      setUsers(list);
      setSelected((prev) => {
        const keys = new Set(list.map((u) => u.userkey));
        const next = new Set();
        prev.forEach((k) => keys.has(k) && next.add(k));
        return next;
      });
    } catch (e) {
      setLoadError(e.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter(
        (u) =>
          (u.userid || "").toLowerCase().includes(q) ||
          (u.username || "").toLowerCase().includes(q) ||
          (u.emailid || "").toLowerCase().includes(q)
      )
    : users;

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageUsers = filteredUsers.slice(pageStart, pageStart + PAGE_SIZE);

  // Reset to page 1 when the search text changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Clamp the page if the filtered list shrinks under our foot (e.g. after delete).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount, page]);

  const toggleOne = (userkey) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userkey)) next.delete(userkey);
      else next.add(userkey);
      return next;
    });

  const toggleAll = () => {
    if (filteredUsers.length === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const allInSelection = filteredUsers.every((u) => next.has(u.userkey));
      if (allInSelection) filteredUsers.forEach((u) => next.delete(u.userkey));
      else filteredUsers.forEach((u) => next.add(u.userkey));
      return next;
    });
  };

  const startAdd = () => {
    setEditUser(null);
    setFormError("");
    setFormMode("add");
  };

  const startEdit = (user) => {
    setEditUser(user);
    setFormError("");
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setEditUser(null);
    setFormError("");
  };

  const submitForm = async (payload) => {
    setSaving(true);
    setFormError("");
    try {
      let res;
      if (formMode === "edit" && editUser) {
        res = await fetch(`${API_BASE_URL}users/${editUser.userkey}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE_URL}users`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      closeForm();
      await reload();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
      setConfirm(null);
      await reload();
    } catch (e) {
      alert(e.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const askDeleteOne = (user) => {
    setConfirm({
      title: "Delete user",
      message: `Delete user "${user.username}"? This cannot be undone.`,
      confirmLabel: "Delete",
      action: async () => {
        const res = await fetch(`${API_BASE_URL}users/${user.userkey}`, { method: "DELETE" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail || `HTTP ${res.status}`);
        }
      },
    });
  };

  const askDeleteSelected = () => {
    const keys = Array.from(selected);
    if (keys.length === 0) return;
    setConfirm({
      title: "Delete selected users",
      message: `Delete ${keys.length} selected user${keys.length === 1 ? "" : "s"}? This cannot be undone.`,
      confirmLabel: `Delete ${keys.length}`,
      action: async () => {
        const res = await fetch(`${API_BASE_URL}users/bulk-delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userkeys: keys }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        setSelected(new Set());
      },
    });
  };

  const allSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selected.has(u.userkey));
  const someSelected = selected.size > 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage users who can access the suite.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-card text-slate-500 transition hover:border-slate-400 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icons.Refresh className={["h-4 w-4", loading ? "animate-spin" : ""].join(" ")} />
            </button>
            <button
              type="button"
              onClick={askDeleteSelected}
              disabled={!someSelected}
              title={someSelected ? `Delete ${selected.size} selected` : "Select rows to delete"}
              aria-label="Delete selected users"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-card text-rose-500 transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icons.Trash className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={startAdd}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            Add user
          </button>
          <HelpButton screen="users" />
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users by id, name or email..."
            aria-label="Search users"
            className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-9 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {loadError}
        </div>
      )}

      {loading && users.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          <Icons.Spinner className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Loading users…
        </div>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          No users yet. Click the plus icon to create one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all users"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    />
                  </th>
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Display Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Created Date</th>
                  <th className="px-4 py-3">Modified Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      No users match "{search}".
                    </td>
                  </tr>
                ) : (
                  pageUsers.map((user) => {
                    const isChecked = selected.has(user.userkey);
                    return (
                      <tr
                        key={user.userkey}
                        className={[
                          "group border-t border-slate-100",
                          isChecked ? "bg-brand-50/40" : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            aria-label={`Select ${user.username}`}
                            checked={isChecked}
                            onChange={() => toggleOne(user.userkey)}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle text-sm font-medium text-slate-900">
                          {user.userid || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {user.username}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {user.emailid}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">
                          {formatDate(user.createddate)}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">
                          {formatDate(user.modifieddate)}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center justify-end gap-1.5">
                            <ActionButton
                              icon={Icons.Eye}
                              label="View"
                              onClick={() => setViewUser(user)}
                            />
                            <ActionButton
                              icon={Icons.Pencil}
                              label="Edit"
                              onClick={() => startEdit(user)}
                            />
                            <ActionButton
                              icon={Icons.Trash}
                              label="Delete"
                              danger
                              onClick={() => askDeleteOne(user)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredUsers.length > 0 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
              <span>
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                  aria-label="Previous page"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-card text-slate-500 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icons.ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-sm text-slate-600">
                  Page {currentPage} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={currentPage === pageCount}
                  title="Next page"
                  aria-label="Next page"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-card text-slate-500 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icons.ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {formMode && (
        <UserFormModal
          mode={formMode}
          initial={editUser}
          onClose={closeForm}
          onSubmit={submitForm}
          saving={saving}
          error={formError}
        />
      )}

      {viewUser && <ViewUserModal user={viewUser} onClose={() => setViewUser(null)} />}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          busy={confirmBusy}
          onCancel={() => (!confirmBusy ? setConfirm(null) : null)}
          onConfirm={runConfirm}
        />
      )}
    </div>
  );
}
